import { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { AISummaryAnswer } from "./ai-summary-answer";
import {
    AISummaryContextProvider,
    useAISummaryContext,
    Source
} from "./ai-summary-context";
import { AISummaryLoadingAnimation } from "./ai-summary-loading-animation";
import { AISummarySources, AISourcesPlaceholder } from "./ai-summary-sources";
import { AIAnswerSearchBorder } from "./styled";

export type { Source };

const aiSummaryLayoutStyle = css({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    width: "100%",
    gap: token("space.150")
});

const aiSummaryLayoutLoadingStyle = css({
    width: "700px"
});

const aiSummaryBoxStyle = css({
    padding: token("space.150"),
    width: "700px",
    maxWidth: "700px"
});

const aiSummarySourcesPanelStyle = css({
    insetBlockStart: "82px",
    insetInlineStart: `calc(100% + ${token("space.150")})`,
    width: "100%",
    maxWidth: "468px",
    position: "sticky"
});

const SOURCES_VIEWPORT_BOTTOM_GUTTER = 16;
const SOURCES_MIN_EXPANDED_HEIGHT = 120;

export const AISummary = ({ query }: { query: string }) => {
    return (
        <AISummaryContextProvider query={query}>
            <AISummaryContent />
        </AISummaryContextProvider>
    );
};

export const AISummaryContent = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(true);
    const [sourcesMaxExpandedHeight, setSourcesMaxExpandedHeight] =
        useState<number>(249);
    const [filteredSourceIndexes, setFilteredSourceIndexes] = useState<
        number[] | null
    >(null);
    const { isLoading, traceResponse, sources, summary } =
        useAISummaryContext();
    const aiSummaryBoxRef = useRef<HTMLDivElement>(null);
    const sourcesPanelRef = useRef<HTMLDivElement>(null);

    const isLoadingOrTraceResponse = Boolean(isLoading || traceResponse);
    const isSourcesLoading = !sources;
    const showSourcesPanel = !isLoadingOrTraceResponse && !!summary;
    const hasNoSources = !isSourcesLoading && sources.length === 0;

    const handleIsExpanded = (isExpanded: boolean) => {
        setIsExpanded(isExpanded);
    };

    const handleIsTruncated = (isTruncated: boolean) => {
        setIsTruncated(isTruncated);
    };

    const handleFilterSources = (sourceIndexes: number[]) => {
        setFilteredSourceIndexes(sourceIndexes);
    };

    const handleClearSourceFilter = () => {
        setFilteredSourceIndexes(null);
    };

    useEffect(() => {
        if (isLoadingOrTraceResponse) {
            return;
        }

        const updateSourcesMaxExpandedHeight = () => {
            const summaryHeight =
                aiSummaryBoxRef.current?.getBoundingClientRect().height || 249;
            const sourcesPanelTop =
                sourcesPanelRef.current?.getBoundingClientRect().top || 0;
            const viewportAvailableHeight = Math.max(
                0,
                window.innerHeight -
                    sourcesPanelTop -
                    SOURCES_VIEWPORT_BOTTOM_GUTTER
            );

            setSourcesMaxExpandedHeight(
                Math.max(
                    SOURCES_MIN_EXPANDED_HEIGHT,
                    Math.floor(Math.min(summaryHeight, viewportAvailableHeight))
                )
            );
        };

        const resizeObserver = new ResizeObserver(() => {
            updateSourcesMaxExpandedHeight();
        });

        if (aiSummaryBoxRef.current) {
            resizeObserver.observe(aiSummaryBoxRef.current);
        }

        window.addEventListener("resize", updateSourcesMaxExpandedHeight);
        updateSourcesMaxExpandedHeight();

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener(
                "resize",
                updateSourcesMaxExpandedHeight
            );
        };
    }, [isExpanded, isLoadingOrTraceResponse]);

    return (
        <div
            css={[
                aiSummaryLayoutStyle,
                isLoadingOrTraceResponse && aiSummaryLayoutLoadingStyle
            ]}
        >
            <AIAnswerSearchBorder
                isLoading={isLoadingOrTraceResponse}
                isStreaming={!!traceResponse}
            >
                <div css={aiSummaryBoxStyle} ref={aiSummaryBoxRef}>
                    {isLoadingOrTraceResponse ? (
                        <AISummaryLoadingAnimation
                            traceResponse={traceResponse}
                        />
                    ) : (
                        <AISummaryAnswer
                            isExpanded={isExpanded}
                            isTruncated={isTruncated}
                            onToggleExpanded={handleIsExpanded}
                            onToggleTruncated={handleIsTruncated}
                            onFilterSources={handleFilterSources}
                            isSourceFiltered={
                                filteredSourceIndexes !== null &&
                                filteredSourceIndexes.length > 0
                            }
                        />
                    )}
                </div>
            </AIAnswerSearchBorder>
            {showSourcesPanel && !hasNoSources && (
                <div css={aiSummarySourcesPanelStyle} ref={sourcesPanelRef}>
                    {isSourcesLoading ? (
                        <AISourcesPlaceholder />
                    ) : (
                        <AISummarySources
                            isExpanded={isExpanded}
                            isTruncated={isTruncated}
                            maxExpandedHeight={sourcesMaxExpandedHeight}
                            filteredSourceIndexes={filteredSourceIndexes}
                            onClearSourceFilter={handleClearSourceFilter}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
